import { useEffect } from "react";
import { Show } from "@refinedev/antd";
import { useCreate, useCustom } from "@refinedev/core";
import {
  Button,
  Checkbox,
  ColorPicker,
  Form,
  Input,
  InputNumber,
  message,
  Select,
} from "antd";
import { AppTitle } from "../../../components";
import { leagueOptions } from "../../Video/leagueOptions";

const DEFAULT_COLORS = ["#ffffff", "#00C7B1", "#ffffff", "#00C7B1", "#00C7B1"];

type TeamOption = {
  teamId: number;
  title: string;
  city?: string;
};

type AnimationFormValues = {
  active: boolean;
  leagueId?: number;
  teamId?: number;
  teamName?: string;
  duration?: number;
  colors: string[];
};

type AnimationPayload = {
  is_active: boolean;
  teamName?: string;
  teamId?: number;
  leagueId?: number;
  duration?: number;
  colors: string[];
};

type ActivityFormResponse = {
  active?: boolean;
  leagueId?: number;
  teamId?: number;
  teamName?: string;
  duration?: number;
  colors?: string[];
};

type ColorInputProps = {
  value?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
};

const normalizeColor = (color: string) => color.trim().toUpperCase();

const isHexColor = (color: string) => /^#[0-9A-Fa-f]{6}$/.test(color);

const ColorInput = ({ value = "", disabled, onChange }: ColorInputProps) => {
  return (
    <Input
      value={value}
      disabled={disabled}
      maxLength={7}
      placeholder="#FFFFFF"
      addonBefore={
        <ColorPicker
          disabled={disabled}
          value={value}
          onChange={(_, hex) => onChange?.(normalizeColor(hex))}
        />
      }
      onChange={(event) => onChange?.(event.target.value)}
      onBlur={(event) => onChange?.(normalizeColor(event.target.value))}
    />
  );
};

export const ChampionAnimationPage = () => {
  const [form] = Form.useForm<AnimationFormValues>();
  const leagueId = Form.useWatch("leagueId", form);

  const { data: activityData, isLoading: activityLoading } =
    useCustom<ActivityFormResponse>({
      dataProviderName: "championAnimationDataProvider",
      url: "activity",
      method: "get",
      errorNotification: () => ({
        message: "Не удалось загрузить статус анимации чемпионов",
        description: "Error",
        type: "error",
      }),
    });

  const { data: teamsData, isLoading: teamsLoading } = useCustom<TeamOption[]>({
    dataProviderName: "championAnimationDataProvider",
    url: "teams",
    method: "get",
    meta: { leagueId },
    errorNotification: () => ({
      message: "Не удалось загрузить команды выбранной лиги",
      description: "Error",
      type: "error",
    }),
  });

  const { mutate, isLoading: saving } = useCreate<AnimationPayload>();
  const teams = teamsData?.data || [];

  useEffect(() => {
    const data = activityData?.data;

    if (!data) {
      return;
    }

    const colors =
      Array.isArray(data.colors) && data.colors.length === 5
        ? data.colors
        : DEFAULT_COLORS;

    form.setFieldsValue({
      active: data.active,
      leagueId: data.leagueId,
      teamId: data.teamId,
      teamName: data.teamName,
      duration: data.duration,
      colors,
    });
  }, [activityData, form]);

  const handleLeagueChange = (leagueId?: number) => {
    form.setFieldsValue({ leagueId, teamId: undefined, teamName: undefined });
  };

  const handleTeamChange = (teamId?: number) => {
    const selectedTeam = teams.find((team) => team.teamId === teamId);
    form.setFieldsValue({
      teamId,
      teamName: selectedTeam?.title,
    });
  };

  const handleSubmit = async (values: AnimationFormValues) => {
    const selectedTeam = teams.find((team) => team.teamId === values.teamId);

    mutate(
      {
        resource: "champions-animation",
        dataProviderName: "championAnimationDataProvider",
        values: {
          active: values.active,
          teamName: selectedTeam?.title || values.teamName,
          teamId: values.teamId,
          leagueId: values.leagueId,
          duration: values.duration,
          colors: values.colors.map(normalizeColor),
        },
      },
      {
        onSuccess: () => {
          message.success("Статус анимации чемпионов обновлен");
        },
        onError: (error: any) => {
          message.error(
            error?.message || "Не удалось обновить статус анимации чемпионов"
          );
        },
      }
    );
  };

  return (
    <Show
      isLoading={activityLoading}
      title={<AppTitle>Анимация чемпионов</AppTitle>}
      breadcrumb={false}
      headerButtons={<></>}
      footerButtons={null}
      goBack={false}
    >
      <Form<AnimationFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          active: false,
          colors: DEFAULT_COLORS,
        }}
        onFinish={handleSubmit}
        style={{ maxWidth: 520 }}
      >
        <Form.Item name="active" valuePropName="checked">
          <Checkbox disabled={saving}>Включить</Checkbox>
        </Form.Item>

        <Form.Item
          name="leagueId"
          label="Лига"
          rules={[{ required: true, message: "Выберите лигу" }]}
        >
          <Select
            allowClear
            disabled={saving}
            options={leagueOptions}
            placeholder="Выберите лигу"
            onChange={handleLeagueChange}
          />
        </Form.Item>

        <Form.Item name="teamName" hidden />

        <Form.Item
          name="teamId"
          label="Команда"
          rules={[{ required: true, message: "Выберите команду" }]}
        >
          <Select
            allowClear
            showSearch
            disabled={!leagueId || saving}
            loading={teamsLoading}
            optionFilterProp="label"
            options={teams.map((team) => ({
              value: team.teamId,
              label: team.city ? `${team.title} (${team.city})` : team.title,
            }))}
            placeholder={
              leagueId ? "Выберите команду" : "Сначала выберите лигу"
            }
            onChange={handleTeamChange}
          />
        </Form.Item>

        <Form.Item
          name="duration"
          label="Длительность анимации"
          rules={[{ required: true, message: "Укажите длительность" }]}
        >
          <InputNumber
            min={1}
            precision={0}
            disabled={saving}
            placeholder="Введите длительность"
            style={{ width: "100%" }}
          />
        </Form.Item>

        {DEFAULT_COLORS.map((_, index) => (
          <Form.Item
            key={index}
            label={index === 0 ? "Цвета" : undefined}
            name={["colors", index]}
            rules={[
              { required: true, message: "Укажите цвет" },
              {
                validator: async (_, value) => {
                  if (!value || isHexColor(value)) {
                    return;
                  }

                  throw new Error("Формат цвета: #FFFFFF");
                },
              },
            ]}
          >
            <ColorInput disabled={saving} />
          </Form.Item>
        ))}

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>
            Сохранить
          </Button>
        </Form.Item>
      </Form>
    </Show>
  );
};
